import React, { useEffect, useMemo, useState } from 'react';
/**
 * MultiplicationGridRenderer — Fill hidden cells in a multiplication table.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { SparklesIcon } from 'lucide-react';
import { QuestionRendererProps } from './types';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function MultiplicationGridRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const mode = config.mode || 'single_table';
  const [filledCells, setFilledCells] = useState<Record<string, string>>({});
  useEffect(() => {
    setFilledCells({});
  }, [question.id]);
  const totalHiddenCells = config.hiddenCells?.length || 0;
  useEffect(() => {
    const filledCount = Object.values(filledCells).filter(v => v.trim() !== '').length;
    onReadyChange?.(filledCount === totalHiddenCells && totalHiddenCells > 0);
  }, [filledCells, totalHiddenCells, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(filledCells), [filledCells]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handleInputChange = (key: string, value: string) => {
    setFilledCells(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const filledCount = Object.values(filledCells).filter(v => v.trim() !== '').length;
  const cellInputClass = (val: string) => `w-full h-full text-center font-bold rounded-md outline-none transition-all duration-200 ${val ? 'bg-indigo-50 border-2 border-indigo-300 text-indigo-800' : 'bg-amber-50 border-2 border-amber-300 text-gray-800'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white shadow-inner`;
  const headerCellClass = 'w-11 h-11 sm:w-12 sm:h-12 bg-indigo-600 text-white font-bold rounded-md text-center align-middle shadow-sm';
  const dataCellClass = 'w-11 h-11 sm:w-12 sm:h-12 bg-white border border-gray-200 text-gray-800 font-medium rounded-md text-center align-middle shadow-sm';
  const renderSingleTable = () => {
    const baseNumber = config.baseNumber || 1;
    const range: number[] = config.range || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const hiddenCells: number[] = config.hiddenCells || [];
    return <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-10816">
        <table className="border-separate border-spacing-1 w-max mx-auto" data-id="element-10817">
          <tbody data-id="element-10818">
            <tr data-id="element-10819">
              <td className="w-11 h-11 sm:w-12 sm:h-12 bg-indigo-800 text-white font-bold rounded-md text-center align-middle shadow-sm" data-id="element-10820">
                ×
              </td>
              {range.map((m, i) => <td key={`h-${i}`} className={headerCellClass} data-id="element-10821">
                  {m}
                </td>)}
            </tr>
            <tr data-id="element-10822">
              <td className={headerCellClass} data-id="element-10823">{baseNumber}</td>
              {range.map((multiplier, idx) => {
              if (hiddenCells.includes(idx)) {
                const key = String(idx);
                return <td key={`c-${idx}`} className="w-11 h-11 sm:w-12 sm:h-12" data-id="element-10824">
                      <input type="text" inputMode="numeric" pattern="[0-9]*" value={filledCells[key] || ''} onChange={e => handleInputChange(key, e.target.value)} className={cellInputClass(filledCells[key] || '')} data-id="element-10825" />
                    </td>;
              }
              return <td key={`c-${idx}`} className={dataCellClass} data-id="element-10826">
                    {baseNumber * multiplier}
                  </td>;
            })}
            </tr>
          </tbody>
        </table>
      </div>;
  };
  const renderCrossTable = () => {
    const rows: number[] = config.rows || [];
    const cols: number[] = config.cols || [];
    const hiddenCells: Array<{
      row: number;
      col: number;
    }> = config.hiddenCells || [];
    const isHidden = (r: number, c: number) => hiddenCells.some(cell => cell.row === r && cell.col === c);
    return <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-10827">
        <table className="border-separate border-spacing-1 w-max mx-auto" data-id="element-10828">
          <tbody data-id="element-10829">
            <tr data-id="element-10830">
              <td className="w-11 h-11 sm:w-12 sm:h-12 bg-indigo-800 text-white font-bold rounded-md text-center align-middle shadow-sm" data-id="element-10831">
                ×
              </td>
              {cols.map((c, i) => <td key={`hc-${i}`} className={headerCellClass} data-id="element-10832">
                  {c}
                </td>)}
            </tr>
            {rows.map((rowVal, rIdx) => <tr key={`r-${rIdx}`} data-id="element-10833">
                <td className={headerCellClass} data-id="element-10834">{rowVal}</td>
                {cols.map((colVal, cIdx) => {
              if (isHidden(rIdx, cIdx)) {
                const key = `${rIdx}-${cIdx}`;
                return <td key={`cc-${rIdx}-${cIdx}`} className="w-11 h-11 sm:w-12 sm:h-12" data-id="element-10835">
                        <input type="text" inputMode="numeric" pattern="[0-9]*" value={filledCells[key] || ''} onChange={e => handleInputChange(key, e.target.value)} className={cellInputClass(filledCells[key] || '')} data-id="element-10836" />
                      </td>;
              }
              return <td key={`cc-${rIdx}-${cIdx}`} className={dataCellClass} data-id="element-10837">
                      {rowVal * colVal}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>;
  };
  const progressPct = totalHiddenCells > 0 ? filledCount / totalHiddenCells * 100 : 0;
  return <div className="w-full flex flex-col space-y-6" data-id="element-10838">
      <div className="w-full bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-100 shadow-inner" data-id="element-10839">
        {mode === 'single_table' ? renderSingleTable() : renderCrossTable()}
      </div>
      <div className="flex flex-col items-center justify-center space-y-2" data-id="element-10840">
        <div className="flex items-center space-x-2 text-sm font-medium text-gray-600" data-id="element-10841">
          <SparklesIcon className={`w-4 h-4 ${filledCount === totalHiddenCells ? 'text-amber-500' : 'text-gray-400'}`} data-id="element-10842" />
          <span data-id="element-10843">
            {filledCount} van {totalHiddenCells} ingevuld
          </span>
        </div>
        <div className="w-48 h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-10844">
          <div className="h-full bg-indigo-500 transition-all duration-300 ease-out" style={{
          width: `${progressPct}%`
        }} data-id="element-10845" />
        </div>
      </div>
    </div>;
}